// Notification Metrics tab — extracted from the legacy SecurityDashboard
// (wave 3: Dashboard tiles and the modern table, Addis 12-hour times).
//
// One-time limitToLast(100) fetch of /notification_metrics, deferred
// until the admin opens the tab (unchanged).

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ref, get, query, limitToLast } from 'firebase/database';
import { FirebaseContext } from 'common';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import SmsFailedOutlinedIcon from '@mui/icons-material/SmsFailedOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import DashboardCard from '../../components/DashboardCard';
import { EmptyState } from '../../components/feedback';
import { DataTable, StatusChip, formatNumber } from '../../components/modern';
import { tokens as c } from '../../theme';

export default function NotificationMetrics() {
  const { t } = useTranslation();
  const { database } = useContext(FirebaseContext);
  const auth = useSelector((state) => state.auth);
  const isAdmin = auth?.profile?.usertype === 'admin';

  const [notificationMetrics, setNotificationMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!database || !isAdmin) return;
    let cancelled = false;
    const fetchMetrics = async () => {
      const metricsQuery = query(ref(database, 'notification_metrics'), limitToLast(100));
      const snapshot = await get(metricsQuery);
      const data = snapshot.val();
      if (cancelled) return;
      if (data) {
        const metricsArray = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        metricsArray.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        setNotificationMetrics(metricsArray);
      } else {
        setNotificationMetrics([]);
      }
      setLoading(false);
    };
    fetchMetrics();
    return () => {
      cancelled = true;
    };
  }, [database, isAdmin]);

  const totals = useMemo(
    () => ({
      totalCampaigns: notificationMetrics.length,
      totalPushSent: notificationMetrics.reduce((sum, m) => sum + (m.push?.sent || 0), 0),
      totalPushFailed: notificationMetrics.reduce((sum, m) => sum + (m.push?.failed || 0), 0),
      totalSmsSent: notificationMetrics.reduce((sum, m) => sum + (m.sms?.sent || 0), 0),
      totalSmsFailed: notificationMetrics.reduce((sum, m) => sum + (m.sms?.failed || 0), 0),
      smsCampaigns: notificationMetrics.filter((m) => m.sendSMS).length,
    }),
    [notificationMetrics]
  );

  if (!isAdmin) {
    return (
      <Paper sx={{ p: 2 }}>
        <EmptyState icon={<LockOutlinedIcon />} title={t('admin_access_required', { defaultValue: 'Admin access required.' })} />
      </Paper>
    );
  }

  const ratio = (part) => `${formatNumber(part?.sent || 0)}/${formatNumber(part?.total || 0)}`;
  const quiet = (text) => (
    <Box component="span" sx={{ color: c.TEXT_3 }}>
      {text}
    </Box>
  );

  const columns = [
    { title: t('date', { defaultValue: 'Date' }), field: 'timestamp', type: 'datetime', defaultSort: 'desc' },
    {
      title: t('title', { defaultValue: 'Title' }),
      field: 'title',
      minWidth: 180,
      render: (m) => (
        <Box component="span" title={m.title || ''} sx={{ display: 'inline-block', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
          {m.title || 'N/A'}
        </Box>
      ),
    },
    { title: t('user_type', { defaultValue: 'User Type' }), field: 'usertype', render: (m) => m.usertype || 'N/A' },
    { title: t('device_type', { defaultValue: 'Device' }), field: 'devicetype', render: (m) => m.devicetype || t('all', { defaultValue: 'All' }) },
    {
      id: 'pushSent',
      title: t('push_sent', { defaultValue: 'Push Sent' }),
      sortValue: (m) => m.push?.sent || 0,
      exportValue: (m) => ratio(m.push),
      render: (m) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <StatusChip tone={(m.push?.failed || 0) > 0 ? 'warning' : 'success'} label={ratio(m.push)} />
          {m.push?.queued ? <StatusChip tone="info" icon={<ScheduleRoundedIcon />} label={t('notif_metrics_queued', { defaultValue: 'Queued' })} /> : null}
        </Box>
      ),
    },
    {
      id: 'pushFailed',
      title: t('push_failed', { defaultValue: 'Push Failed' }),
      type: 'number',
      sortValue: (m) => m.push?.failed || 0,
      exportValue: (m) => m.push?.failed || 0,
      render: (m) => ((m.push?.failed || 0) > 0 ? <StatusChip tone="error" label={formatNumber(m.push.failed)} /> : quiet('0')),
    },
    {
      id: 'smsSent',
      title: t('sms_sent', { defaultValue: 'SMS Sent' }),
      sortValue: (m) => (m.sendSMS ? m.sms?.sent || 0 : -1),
      exportValue: (m) => (m.sendSMS ? ratio(m.sms) : ''),
      render: (m) => (m.sendSMS ? <StatusChip tone={(m.sms?.failed || 0) > 0 ? 'warning' : 'success'} label={ratio(m.sms)} /> : quiet('—')),
    },
    {
      id: 'smsFailed',
      title: t('sms_failed_count', { defaultValue: 'SMS Failed' }),
      type: 'number',
      sortValue: (m) => (m.sendSMS ? m.sms?.failed || 0 : -1),
      exportValue: (m) => (m.sendSMS ? m.sms?.failed || 0 : ''),
      render: (m) =>
        m.sendSMS && (m.sms?.failed || 0) > 0 ? (
          <Tooltip title={(m.sms?.errors || []).join(', ') || t('notif_metrics_unknown_errors', { defaultValue: 'Unknown errors' })}>
            <span>
              <StatusChip tone="error" label={formatNumber(m.sms.failed)} />
            </span>
          </Tooltip>
        ) : (
          quiet(m.sendSMS ? '0' : '—')
        ),
    },
  ];

  const tile = (key, icon, hue, label, value) => (
    <DashboardCard key={key} icon={icon} hue={hue} title={label} value={formatNumber(value)} loading={loading} />
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' } }}>
        {tile('campaigns', <CampaignOutlinedIcon />, 'accent', t('total_campaigns', { defaultValue: 'Total Campaigns' }), totals.totalCampaigns)}
        {tile('pushSent', <NotificationsActiveOutlinedIcon />, 'success', t('push_sent', { defaultValue: 'Push Sent' }), totals.totalPushSent)}
        {tile('pushFailed', <WarningAmberRoundedIcon />, 'danger', t('push_failed', { defaultValue: 'Push Failed' }), totals.totalPushFailed)}
        {tile('smsSent', <SmsOutlinedIcon />, 'info', t('sms_sent', { defaultValue: 'SMS Sent' }), totals.totalSmsSent)}
        {tile('smsFailed', <SmsFailedOutlinedIcon />, 'amber', t('sms_failed_count', { defaultValue: 'SMS Failed' }), totals.totalSmsFailed)}
        {tile('smsCampaigns', <SmsOutlinedIcon />, 'neutral', t('notif_metrics_sms_campaigns', { defaultValue: 'SMS Campaigns' }), totals.smsCampaigns)}
      </Box>

      <DataTable
        title={t('notifications', { defaultValue: 'Notifications' })}
        rows={notificationMetrics}
        columns={columns}
        rowKey="id"
        loading={loading}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
              : t('notif_metrics_count', {
                  count: total,
                  defaultValue_one: '{{count}} campaign (last 100)',
                  defaultValue_other: '{{count}} campaigns (last 100)',
                  defaultValue: '{{count}} campaigns (last 100)',
                })
        }
        searchPlaceholder={t('notif_metrics_search', { defaultValue: 'Search title or user type' })}
        rowLabel={(m) => m.title}
        prefsKey="security_notification_metrics"
        minWidth={980}
        maxHeight="calc(100vh - 360px)"
        emptyState={({ query }) =>
          query ? null : { icon: <CampaignOutlinedIcon />, title: t('notif_metrics_empty', { defaultValue: 'No notification campaigns yet' }) }
        }
      />
    </Box>
  );
}
